// Used in notifications.module.ts

// Angular
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { MatTabChangeEvent } from "@angular/material/tabs";

// Moment
import moment from "moment";

// Services/Types/Helpers
import { Notification } from "cli-types";

// RXJS/Store
import * as messagesStore from "#core/notifications/store";
import * as fromStore from "#login/store";
import { USER_SERVICE } from "@carnegie-learning/data-access-angular-fido";
import { NotificationPagination, NotificationService } from "@carnegie-learning/data-access-angular-passport";
import { UserService } from "@carnegie-learning/data-access-fido";
import { Store, select } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";

@Component({
    selector: "notifications",
    templateUrl: "./notifications.component.html",
    styleUrls: ["../../notifications.component.scss", "./notifications.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class NotificationsComponent implements OnInit, AfterViewInit, OnDestroy {
    private ngUnsubscribe = new Subject();
    public notifications?: Notification[];
    public pagedNotifications?: NotificationPagination;

    public notificationsArchived?: Notification[];
    public pagedNotificationsArchived?: NotificationPagination;

    public notificationsSent?: Notification[];
    public pagedNotificationsSent?: NotificationPagination;

    // Page index counts from zero to be compatible with mat-paginator.
    // The API counts from 1.
    DEFAULT_PAGE_INDEX = 0;
    DEFAULT_PAGE_SIZE = 10;
    public pageIndex = this.DEFAULT_PAGE_INDEX;
    public pageSize = this.DEFAULT_PAGE_SIZE;
    public pageSizeOptions = [5, 10, 20, 50];

    isTeacher$?: Observable<boolean>;
    isStudent$?: Observable<boolean>;
    isDistributor$?: Observable<boolean>;
    public selectedTab = 0;

    unreadCount: Observable<number>;

    loading = false;

    @Output() sidePanelRightClose = new EventEmitter<void>();

    constructor(
        private notificationService: NotificationService,
        public store: Store<fromStore.UserState>,
        private cdr: ChangeDetectorRef,
        private store_messages: Store<messagesStore.NotificationsState>,
        @Inject(USER_SERVICE) private userService: UserService
    ) {
        this.unreadCount = this.store.pipe(select(fromStore.getUnreadNotificationsCount));
    }

    closeSideNav() {
        this.sidePanelRightClose.emit();
    }

    ngOnInit() {
        this.store_messages
            .pipe(select(messagesStore.getMessagesTab))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((messagesTab) => {
                if (messagesTab) {
                    this.selectedTab = messagesTab;
                }
            });

        this.isDistributor$ = this.store.pipe(select(fromStore.getIsDistributor));

        this.isTeacher$ = this.store.pipe(select(fromStore.getIsTeacher));

        this.isStudent$ = this.store.pipe(select(fromStore.isStudent));
    }

    ngAfterViewInit() {
        const type = this.getType();
        this.getNotifications(type);
    }

    getType() {
        let type: string;
        if (this.selectedTab === 0) {
            type = "inbox";
        } else if (this.selectedTab === 1) {
            type = "sent";
        } else if (this.selectedTab === 2) {
            type = "archived";
        } else {
            type = "inbox";
        }
        return type;
    }

    getNotifications(type: string, pageEvent: PageEvent | null = null) {
        if (pageEvent) {
            this.pageSize = pageEvent.pageSize;
            this.pageIndex = pageEvent.pageIndex;
        } else if (!this.pageIndex) {
            this.pageIndex = this.DEFAULT_PAGE_INDEX;
        }

        if (this.pageSize && !pageEvent) {
            this.pageIndex = this.DEFAULT_PAGE_INDEX;
            pageEvent = {
                length: 35,
                pageIndex: 0,
                pageSize: this.pageSize,
                previousPageIndex: 0,
            };
        }
        const pageDetails: { pageSize: number; pageIndex: number } = {
            pageSize: this.pageSize,
            pageIndex: this.pageIndex,
        };
        this.loading = true;
        this.cdr.detectChanges();
        this.notificationService.getNotifications(type, pageDetails).subscribe((response) => {
            if (response.body) {
                response.body.data.forEach((n) => {
                    const hrefString = "href=", // deliberately leave off the opening quote, as we don't know what kind of quote it might be
                        hrefStringLength = hrefString.length + 1, // add one for the opening quote omitted from the previous string
                        prependStr = "http://",
                        prependStrLength = prependStr.length;
                    let indexOfHref = n.body.indexOf(hrefString);
                    // test for the presence of an href that does NOT BEGIN with http...
                    while (
                        indexOfHref > -1 &&
                        n.body.slice(indexOfHref + hrefStringLength).indexOf("http") !== 0
                    ) {
                        n.body =
                            n.body.slice(0, indexOfHref + hrefStringLength) +
                            prependStr +
                            n.body.slice(indexOfHref + hrefStringLength); // ...fix it.
                        if (
                            n.body
                                .substring(indexOfHref + hrefStringLength + prependStrLength)
                                .indexOf(hrefString) > 0
                        ) {
                            // still more...keep looping
                            indexOfHref =
                                n.body
                                    .substring(indexOfHref + hrefStringLength + prependStrLength)
                                    .indexOf(hrefString) +
                                indexOfHref +
                                hrefStringLength +
                                prependStrLength;
                            // ^ reset the indexOfHref to just past the one that we just fixed
                        } else {
                            indexOfHref = -1; // no more. stop looping
                        }
                    }
                });
                switch (type) {
                    case "sent":
                        this.pagedNotificationsSent = response.body;
                        this.getRecipientNames(this.pagedNotificationsSent.data, type);
                        break;
                    case "archived":
                        this.pagedNotificationsArchived = response.body;
                        this.getAuthorNames(this.pagedNotificationsArchived.data, type);
                        break;
                    case "inbox":
                        this.pagedNotifications = response.body;
                        this.getAuthorNames(this.pagedNotifications.data, type);
                        break;
                }
                this.store.dispatch(
                    fromStore.userProfileSetUnreadNotificationCount({
                        payload: response.body.unreadNotificationsCount,
                    })
                );
            }
        });
    }

    getAuthorNames(notes: Notification[], type: string) {
        const authorGUIDs = notes.map((n) => n.author.userGUID);
        this.userService
            .getUsers(authorGUIDs)
            .pipe(
                map((fidoUsers) => {
                    notes = notes.map((n) => {
                        const foundUser = fidoUsers.find((u) => u.id === n.author.userGUID);
                        return {
                            ...n,
                            authorName: foundUser?.givenName + " " + foundUser?.surName,
                        };
                    });
                    switch (type) {
                        case "inbox":
                            this.notifications = notes;
                            break;
                        case "archived":
                            this.notificationsArchived = notes;
                            break;
                        case "sent":
                            this.notificationsSent = notes;
                            break;
                    }
                    this.loading = false;
                })
            )
            .subscribe();
    }

    getRecipientNames(notes: Notification[], type: string) {
        const recipientGUIDs = notes.map((n) => n.recipient?.userGUID).filter((guids) => guids);
        this.userService
            .getUsers(recipientGUIDs)
            .pipe(
                map((fidoUsers) => {
                    notes = notes.map((n) => {
                        const foundUser = fidoUsers.find((u) => u.id === n.recipient?.userGUID);
                        if (foundUser) {
                            return {
                                ...n,
                                recipient: {
                                    ...n.recipient,
                                    firstName: foundUser.givenName,
                                    lastName: foundUser.surName,
                                    fullName: foundUser.givenName + " " + foundUser.surName,
                                },
                            };
                        }
                        return n;
                    });
                    this.notificationsSent = notes;
                    this.loading = false;
                })
            )
            .subscribe();
    }

    markAsRead(notification: Notification) {
        if (!notification.readAt) {
            this.store.dispatch(fromStore.userProfileDecrementUnreadNotificationCount());
            notification.readAt = moment.now().toLocaleString();
            this.notificationService.markAsRead(notification).subscribe();
        }
    }

    archive(notification: Notification) {
        if (this.notifications) {
            const index = this.notifications.indexOf(notification);
            this.notifications.splice(index, 1);
            this.notificationService.archive(notification).subscribe((data) => {
                const type = this.getType();
                this.getNotifications(type, null);
            });
        }
    }

    restore(notification: Notification) {
        if (this.notificationsArchived) {
            const index = this.notificationsArchived.indexOf(notification);
            this.notificationsArchived.splice(index, 1);
            this.notificationService.restore(notification).subscribe((data) => {
                const type = this.getType();
                this.getNotifications(type, null);
            });
        }
    }

    onSelectChange(event: MatTabChangeEvent) {
        if (event.index === 3) {
            // Create New
            this.selectedTab = 3;
        } else if (event.index === 0) {
            // Inbox
            this.selectedTab = 0;
            this.getNotifications("inbox");
        } else if (event.index === 1) {
            // Sent
            this.selectedTab = 1;
            this.getNotifications("sent");
        } else if (event.index === 2) {
            // Archived
            this.selectedTab = 2;
            this.getNotifications("archived");
        }
    }

    goToInbox() {
        this.selectedTab = 0;
        this.getNotifications("inbox");
    }

    sendReply(notification: Notification) {
        this.notificationService.sendPm(notification).subscribe((data) => {
            if (this.notifications) {
                // Update the most recent reply in the list!
                const repliedToNotification = this.notifications.find(
                    (inReplyTo) => inReplyTo.noticeID === notification.inReplyToNoticeID
                );
                repliedToNotification?.replies.unshift(data);
            }
        });
    }

    saveEdit(notification: Notification) {
        notification.displayAt = notification.displayAt
            ? moment(notification.displayAt).format("YYYY-MM-DD HH:mm:ss")
            : null;
        notification.expireAt = notification.expireAt
            ? moment(notification.expireAt).format("YYYY-MM-DD HH:mm:ss")
            : null;

        // Note: You can only edit sent notifications, so therefore, we only need to update the notificationsSent array.
        this.notificationService.saveEdit(notification).subscribe((data) => {
            if (this.notificationsSent) {
                const index = this.notificationsSent.findIndex(
                    (edited) => edited.noticeID === notification.noticeID
                );
                this.notificationsSent[index] = data;
            }
        });
    }

    deleteNotification(notification: Notification) {
        if (this.notificationsSent) {
            // Note: You can only edit sent notifications, so therefore, we only need to update the notificationsSent array.
            const index = this.notificationsSent.findIndex(
                (edited) => edited.noticeID === notification.noticeID
            );
            this.notificationsSent.splice(index, 1);
            this.notificationService.deleteNotification(notification).subscribe((data) => {
                const type = this.getType();
                this.getNotifications(type, null);
            });
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
